import type { ReactNode, RefObject } from 'react'
import { useEffect, useRef, useState } from 'react'
import { TbArrowUpRight, TbChevronRight } from 'react-icons/tb'
import clsx from 'clsx'
import type { StackProps } from '~/components-v2'
import { Stack } from '~/components-v2'
import { useClickAnyWhere, useHover } from 'usehooks-ts'
import { JSX } from 'react'
import { SquaredIcon } from '~/components-v2/squaredIcon/squaredIcon'

type NavCardContentProps = Pick<
  StackProps,
  'className' | 'gap' | 'row' | 'wrap'
>

type NavCardProps = {
  className?: string
  children?: ReactNode
  title: string | JSX.Element
  subTitle?: string | JSX.Element
  icon?: JSX.Element | null
  contentProps?: NavCardContentProps
  externalLink?: boolean
  to?: string
}
export const NavCard = ({
  className,
  title,
  icon,
  subTitle,
  children,
  externalLink,
  to,
}: NavCardProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const ref = useRef<HTMLDetailsElement>(null)
  const summaryRef = useRef<HTMLElement>(null)
  const isTouchDevice =
    typeof window !== 'undefined' &&
    window.matchMedia('(pointer: coarse)').matches
  const isHover = useHover(ref as RefObject<HTMLDetailsElement>)
  const isParentItem = Boolean(children)

  useEffect(() => {
    if (!isTouchDevice) setIsOpen(isHover)
  }, [isHover, isTouchDevice])

  useClickAnyWhere((event) => {
    if (!isOpen) return
    if (!ref.current || !summaryRef.current) return
    if (summaryRef.current.contains(event.target as Node)) return
    setIsOpen(false)
  })

  return (
    <>
      <Stack row className={clsx(className)} gap={5}>
        {externalLink ? (
          <a
            target="_blank"
            rel="noreferrer"
            href={to}
            className={clsx(
              subTitle ? 'items-start' : 'items-center',
              'nav-card-content external-link-card',
            )}
          >
            <span className="details">
              <span className="summary">
                {icon && <SquaredIcon icon={icon} />}
                <article>
                  <span>
                    <h3>{title}</h3>
                    {externalLink && <TbArrowUpRight />}
                  </span>
                  {subTitle && <p>{subTitle}</p>}
                </article>

                {children && <TbChevronRight className="nav-drawer-arrow" />}
                {isParentItem && (
                  <Stack className="nav-drawer">{children}</Stack>
                )}
              </span>
            </span>
          </a>
        ) : (
          <details
            className={clsx(!to && 'labelOnly')}
            ref={ref}
            open={isParentItem && isOpen}
          >
            <summary
              className={clsx(
                subTitle ? 'items-start' : 'items-center',
                'nav-card-content',
              )}
              ref={summaryRef}
              onClick={(e) => {
                const isLabelOnly = e.currentTarget
                  .closest('details')
                  ?.classList.contains('labelOnly')
                if (isLabelOnly) {
                  e.preventDefault()
                  e.stopPropagation()
                }
                if (isTouchDevice) {
                  e.preventDefault()
                  setIsOpen((prev) => !prev)
                }
              }}
            >
              {icon && <SquaredIcon icon={icon} />}
              <article>
                <h3>{title}</h3>
                {subTitle && <p>{subTitle}</p>}
              </article>
              {children && <TbChevronRight className="nav-drawer-arrow" />}
            </summary>
            {isParentItem && <Stack className="nav-drawer">{children}</Stack>}
          </details>
        )}
      </Stack>
    </>
  )
}
